import { observer } from "mobx-react";
import usePage from "../../../../../../components/layouts/base-page-layout/page-hook";
import { useCallback, useEffect, useState } from "react";
import useTranslation from "../../../../../../translation/hooks/translation-hook";
import useNavigationBlocker from "../../../../../../hooks/navigation-blocker-hook";
import TranslationScope from "../../../../../../translation/translation-scope";
import EditorPageLayout from "../../../../../../components/layouts/editor-page/editor-page-layout";
import { ConfirmModal } from "../../../../../../components/modal/components/confirm-modal/confirm-modal";
import ControlGroupWithPreview from "../../../../../../components/layouts/control-group-with-preview/control-group-with-preview";
import ListLayout from "../../../../../../components/layouts/list-layout/list-layout";
import { FormField } from "../../../../../../components/form-field/form-field";
import TranslationInput from "../../../../../../translation/translation-input/translation-input";
import { RETURNS_PORTAL_TRANSLATION_KEYS } from "../../constants";
import SelectItem1Preview from '../../../../../../images/previews/select-item-1.svg';
import SelectItem2Preview from '../../../../../../images/previews/select-item-2.svg';

const SelectItemsEditorPage: React.FC = observer(() => {
    const { setSubTitles } = usePage();
    const [isShowUnsaved, setIsShowUnsaved] = useState(false);

    useEffect(() => {
        setSubTitles(['Portal', 'Content', 'Return Portal', 'Select Items']);
    }, [setSubTitles]);

    const {
        hasChanges: hasTranslationChanges,
        discardChanges: translationsDiscardChanges,
        save: saveTranslations,
        selectedLocale
    } = useTranslation();

    const hasChanges = () => {
        return hasTranslationChanges();
    }

    const { retryNavigation, unblockNavigation } = useNavigationBlocker(
        () => hasChanges(),
        useCallback(() => setIsShowUnsaved(true), [setIsShowUnsaved])
    );

    const discardChanges = () => {
        translationsDiscardChanges();
    }

    const onSaveClicked = () => {
        const promised = [];

        if (hasTranslationChanges()) {
            promised.push(
                saveTranslations()
            );
        }

        Promise.all(promised).then(() => {
            unblockNavigation();
        })
    }

    return (
        <TranslationScope scope="assistant" locale={selectedLocale}>
            <EditorPageLayout
                toolbar={{
                    saveButton: {
                        hasChanges: hasChanges(),
                        disabled: !hasChanges(),
                        onClick: onSaveClicked
                    }
                }}
            >
                <ListLayout>
                    <ControlGroupWithPreview
                        preview={<ListLayout>
                            <img src={SelectItem1Preview} alt="" />
                        </ListLayout>}
                    >
                        <ListLayout>
                            <FormField label="Headline">
                                <TranslationInput translationKey={RETURNS_PORTAL_TRANSLATION_KEYS.WHICH_ITEMS_YOU_WANT_TO_RETURN} />
                            </FormField>

                            <FormField label="Instructions">
                                <TranslationInput translationKey={RETURNS_PORTAL_TRANSLATION_KEYS.CHOOSE_ANY_ITEMS} />
                            </FormField>

                            <FormField label="Select Amount">
                                <TranslationInput translationKey={RETURNS_PORTAL_TRANSLATION_KEYS.SELECT_AMOUNT} />
                            </FormField>

                            <FormField label="Select Action Placeholder">
                                <TranslationInput translationKey={RETURNS_PORTAL_TRANSLATION_KEYS.SELECT_ACTION} />
                            </FormField>

                            <FormField label="Replace Description">
                                <TranslationInput translationKey={RETURNS_PORTAL_TRANSLATION_KEYS.ITEM_ACTION_EXCHANGE_REPLACE_DESCRIPTION} />
                            </FormField>

                            <FormField label="Variant Exchange Description">
                                <TranslationInput translationKey={RETURNS_PORTAL_TRANSLATION_KEYS.ITEM_ACTION_EXCHANGE_DESCRIPTION} />
                            </FormField>

                            <FormField label="Product Exchange Description">
                                <TranslationInput translationKey={RETURNS_PORTAL_TRANSLATION_KEYS.ITEM_ACTION_PRODUCT_EXCHANGE_DESCRIPTION} />
                            </FormField>

                            <FormField label="Manual Exchange Description">
                                <TranslationInput translationKey={RETURNS_PORTAL_TRANSLATION_KEYS.ITEM_ACTION_MANUAL_EXCHANGE_DESCRIPTION} />
                            </FormField>

                            <FormField label="Return (Refund) Description">
                                <TranslationInput translationKey={RETURNS_PORTAL_TRANSLATION_KEYS.ITEM_ACTION_RETURN_DESCRIPTION} />
                            </FormField>

                            <FormField label="Multiple Items Options">
                                <ListLayout>
                                    <TranslationInput translationKey={RETURNS_PORTAL_TRANSLATION_KEYS.GROUP_ITEM_ACTION_SAME} />
                                    <TranslationInput translationKey={RETURNS_PORTAL_TRANSLATION_KEYS.GROUP_ITEM_ACTION_SEPARATE} />
                                </ListLayout>
                            </FormField>
                            
                            <FormField label="Portal Covered Order Message Title">
                                <TranslationInput translationKey={RETURNS_PORTAL_TRANSLATION_KEYS.YOUR_RETURN_IS_FREE} />
                            </FormField>

                            <FormField label="Portal Covered Order Message Description">
                                <TranslationInput translationKey={RETURNS_PORTAL_TRANSLATION_KEYS.YOUR_RETURN_IS_FREE_DESCRIPTION} />
                            </FormField>
                        </ListLayout>
                    </ControlGroupWithPreview>
                    <ControlGroupWithPreview
                        preview={<ListLayout>
                            <img src={SelectItem2Preview} alt="" />
                        </ListLayout>}
                    >
                        <ListLayout>
                            <FormField
                                instructions={"This note will appear when items have been removed from the order and refunded. Insert the portal variable :removed_quantity where you want it to appear in the message."}
                                label="Information About Removed Items">
                                <TranslationInput translationKey={RETURNS_PORTAL_TRANSLATION_KEYS.REMOVED_ITEMS_INFO_NOTE} />
                            </FormField>
                        </ListLayout>
                    </ControlGroupWithPreview>
                </ListLayout>
            </EditorPageLayout>

            {isShowUnsaved &&
                <ConfirmModal
                    cancelButton={{
                        title: 'Cancel',
                        onClick: () => setIsShowUnsaved(false)
                    }}
                    okButton={{
                        title: 'Discard',
                        onClick: () => {
                            discardChanges();
                            setIsShowUnsaved(false);
                            retryNavigation();
                        }
                    }}
                />
            }
        </TranslationScope>
    );
});

export default SelectItemsEditorPage;